import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { Icons } from '../shared/icons';
import { FEATURE_GROUPS, COMPARISON, UNIQUE } from './content';

function CompareCell({ value, us }) {
  const cls = us ? 'mk-col-us' : '';
  if (value === true) return <td className={`${cls} mk-yes`.trim()}>✓</td>;
  if (value === false) return <td className={`${cls} mk-no`.trim()}>✗</td>;
  return <td className={`${cls} mk-muted`.trim()}>{value}</td>;
}

export default function Features() {
  const [query, setQuery] = useState('');

  const groups = query.trim()
    ? FEATURE_GROUPS.map((g) => ({
        ...g,
        items: g.items.filter((item) => item.toLowerCase().includes(query.toLowerCase()))
      })).filter((g) => g.items.length > 0)
    : FEATURE_GROUPS;

  return (
    <>
      {/* ---------------- hero ---------------- */}
      <section className="mk-hero">
        <div className="mk-container">
          <span className="mk-hero-badge">
            <SafeIcon icon={Icons.List} />
            Everything the platform does — 30+ screens, one login
          </span>
          <h1>
            Every feature, <em>end to end</em>
          </h1>
          <p className="mk-hero-sub">
            From the first quote to the filed certificate: admin console, client portal
            and engineer PWA — with payments, notifications, AI assistance, mapping and a
            full audit trail built in.
          </p>
          <div className="mk-hero-ctas">
            <Link to="/pricing" className="mk-btn mk-btn-primary">
              See what it costs
              <SafeIcon icon={Icons.ArrowRight} />
            </Link>
            <Link to="/sign-up" className="mk-btn mk-btn-outline">
              Book a demo
            </Link>
          </div>
        </div>
      </section>

      {/* ---------------- searchable inventory ---------------- */}
      <section className="mk-section">
        <div className="mk-container">
          <div className="mk-section-head" style={{ maxWidth: '100%' }}>
            <span className="mk-eyebrow">Full inventory</span>
            <h2>Search the platform</h2>
            <div className="mk-search">
              <input
                type="search"
                value={query}
                placeholder="Try “certificate”, “stripe”, “telegram”, “offline”…"
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search features"
              />
            </div>
          </div>

          {groups.map((g) => (
            <div className="mk-feature-group" key={g.title} style={{ marginBottom: 34 }}>
              <h3 className="mk-group-title">
                <span className="mk-card-icon"><SafeIcon icon={Icons[g.icon]} /></span>
                {g.title}
              </h3>
              <div className="mk-group-grid">
                {g.items.map((item) => (
                  <div className="mk-feature-item" key={item}>
                    <SafeIcon icon={Icons.CheckCircle} />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {groups.length === 0 && (
            <p className="mk-hero-note" style={{ padding: '20px 0 60px' }}>
              Nothing matches “{query}” — try another term, or ask us directly from the sign-up page.
            </p>
          )}
        </div>
      </section>

      {/* ---------------- comparison ---------------- */}
      <section className="mk-section" style={{ background: '#fff', borderTop: '1px solid var(--mk-line)', borderBottom: '1px solid var(--mk-line)' }}>
        <div className="mk-container">
          <div className="mk-section-head">
            <span className="mk-eyebrow">How we compare</span>
            <h2>portal-core vs the usual suspects</h2>
            <p>
              Feature data from our 2026 competitive review (docs/pricing.md §3). “Limited”
              means the competitor offers a reduced version of the capability.
            </p>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="mk-compare">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th className="mk-col-us">during.uk</th>
                  <th>Jobber</th>
                  <th>Housecall Pro</th>
                  <th>ServiceM8</th>
                  <th>SimPRO</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row) => (
                  <tr key={row.feature}>
                    <td>{row.feature}</td>
                    <CompareCell value={row.us} us />
                    <CompareCell value={row.jobber} />
                    <CompareCell value={row.hcp} />
                    <CompareCell value={row.s8} />
                    <CompareCell value={row.simpro} />
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mk-compare-foot" style={{ marginTop: 12, fontSize: 13, color: 'var(--muted, #6d7774)' }}>
              Sourced from our 2026 competitive review (docs/pricing.md §3) — competitor
              capabilities re-checked August 2026. Our cells reflect live, tested
              behaviour only.
            </p>
          </div>
        </div>
      </section>

      {/* ---------------- unique band ---------------- */}
      <section className="mk-section mk-unique">
        <div className="mk-container">
          <div className="mk-section-head">
            <span className="mk-eyebrow">Unique to us</span>
            <h2>These five exist nowhere else</h2>
            <p>
              Not “coming soon” — shipped, live and part of every Professional plan
              onwards.
            </p>
          </div>
          <div className="mk-unique-grid">
            {UNIQUE.map((u) => (
              <div className="mk-unique-item" key={u.title}>
                <SafeIcon icon={Icons[u.icon]} />
                <h3>{u.title}</h3>
                <p>{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- CTA ---------------- */}
      <section className="mk-cta-band">
        <div className="mk-container">
          <h2>See it on your own branding</h2>
          <p>
            Every plan includes the full feature inventory above. Professional adds the
            engineer portal and certificates.
          </p>
          <div className="mk-hero-ctas">
            <Link to="/sign-up" className="mk-btn mk-btn-lime">
              Book a demo
              <SafeIcon icon={Icons.ArrowRight} />
            </Link>
            <Link to="/pricing" className="mk-btn mk-btn-outline">
              Compare plans
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
